/* eslint-disable no-underscore-dangle */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { HTMLAttributes, useState, useEffect } from 'react';
import { AnimatePresence, motion as m } from 'framer-motion';
import { IRegisterOutputProps } from '@xmanscript/useform/lib/@types';
import { cn } from '@/utils';

import FormControl from '../FormControl';

interface INameEditProps extends HTMLAttributes<HTMLDivElement> {
  registerProps: IRegisterOutputProps;
}

export default function CategoryEdit({ className, registerProps, ...props }: INameEditProps) {
  const [isEditable, setIsEditable] = useState(false);
  const [tempSelection, setTempSelection] = useState(registerProps.value);

  // Update tempSelection when registerProps.value changes
  useEffect(() => {
    setTempSelection(registerProps.value);
  }, [registerProps.value]);

  // Hardcoded categories instead of fetching from API
  const hardcodedCategories = [
    { _id: 'tech', id: 'tech', name: 'Technology', label: 'Technology' },
    { _id: 'business', id: 'business', name: 'Business', label: 'Business' },
    { _id: 'education', id: 'education', name: 'Education', label: 'Education' },
    { _id: 'health', id: 'health', name: 'Health', label: 'Health' },
    { _id: 'entertainment', id: 'entertainment', name: 'Entertainment', label: 'Entertainment' },
    { _id: 'lifestyle', id: 'lifestyle', name: 'Lifestyle', label: 'Lifestyle' },
    { _id: 'travel', id: 'travel', name: 'Travel', label: 'Travel' },
    { _id: 'food', id: 'food', name: 'Food', label: 'Food' },
  ];

  const fetchedCategories = { result: hardcodedCategories };

  return (
    <>
      <div className={cn('flex w-full flex-col items-center justify-center gap-2', className)} {...props}>
        <div className={cn('flex w-full items-center justify-center gap-5', className)} {...props}>
          <p className="w-[6rem] text-[0.875rem] font-normal text-[#667085]">Category</p>
          <div className="flex flex-1 items-center justify-between gap-2 py-[0.38rem] pr-3">
            <p
              className="flex-1 p-2 font-inter text-sm font-semibold text-[#475467]"
              style={{ borderBottom: '1px solid var(--Grey-G-400, #BDBDBD)' }}
            >
              {fetchedCategories?.result.find(
                (category: Record<string, any>) =>
                  category._id === registerProps.value?.[0] || category.id === registerProps.value?.[0],
              )?.name || 'Select a category'}
            </p>

            <button
              onClick={() => {
                setIsEditable((prev) => !prev);
                const doc = document.getElementById(registerProps.id);
                doc?.focus();
              }}
              type="button"
              className="border-none p-2 outline-none"
            >
              <img src="/edit.svg" alt="edit" className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      {isEditable && (
        <AnimatePresence mode="wait">
          <m.div
            initial={{ opacity: 0, translateY: '100%' }}
            animate={{ opacity: 1, translateY: '0' }}
            exit={{ opacity: 0, translateY: '100%' }}
            key="drawyer"
            onClick={() => {
              setIsEditable((prev) => !prev);
            }}
            className="absolute left-0 top-0 z-[100] flex h-full w-full items-end justify-center bg-black bg-opacity-30 backdrop-blur-[1px]"
          >
            <div
              className="flex min-h-[8rem] w-full flex-col gap-6 rounded-t-2xl bg-white px-4 pb-5 pt-4"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="flex w-full items-center justify-center p-2"
                onClick={() => {
                  setIsEditable((prev) => !prev);
                }}
                type="button"
              >
                <div
                  onClick={() => {
                    setIsEditable((prev) => !prev);
                  }}
                  className="h-[0.375rem] w-[3.25rem]"
                  style={{ borderRadius: '1.25rem', background: '#D7D7D7' }}
                >
                  &nbsp;
                </div>
              </button>
              {fetchedCategories && fetchedCategories?.result.length ? (
                <>
                  <FormControl
                    controlType="chipSelector"
                    options={fetchedCategories?.result || []}
                    choose="_id"
                    className="w-full"
                    {...registerProps}
                    value={tempSelection}
                    onChange={(value) => setTempSelection(value)}
                  />
                  {(() => {
                    if (!tempSelection || (Array.isArray(tempSelection) && tempSelection.length === 0)) {
                      return <p className="text-sm text-red-500">One category must be selected</p>;
                    }
                    if (Array.isArray(tempSelection) && tempSelection.length > 1) {
                      return <p className="text-sm text-red-500">Only one category must be selected</p>;
                    }
                    return null;
                  })()}
                  <button
                    type="button"
                    onClick={() => {
                      // Validate before saving
                      if (!tempSelection || (Array.isArray(tempSelection) && tempSelection.length === 0)) {
                        return; // Don't close drawer on validation error
                      }
                      if (Array.isArray(tempSelection) && tempSelection.length > 1) {
                        return; // Don't close drawer on validation error
                      }

                      // Apply temporary selection to actual form value
                      registerProps.onChange(tempSelection);
                      // Also dispatch a custom event with the new category value
                      const categoryChangeEvent = new CustomEvent('categoryChange', {
                        detail: { categories: tempSelection },
                      });
                      window.dispatchEvent(categoryChangeEvent);
                      // Trigger form submission from parent
                      const event = new CustomEvent('categorySubmit');
                      window.dispatchEvent(event);
                      setIsEditable(false);
                    }}
                    className="h-12 w-full rounded-lg bg-sky-500 font-medium text-white transition-colors hover:bg-sky-600 disabled:bg-gray-400"
                    disabled={
                      !tempSelection ||
                      (Array.isArray(tempSelection) && (tempSelection.length === 0 || tempSelection.length > 1))
                    }
                  >
                    Save Changes
                  </button>
                </>
              ) : (
                <p className="w-full text-left">No Categories Found.</p>
              )}
            </div>
          </m.div>
        </AnimatePresence>
      )}
    </>
  );
}
